import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  getLensCups, getLensCupSchema,
  getDataSource, createDataSource, updateDataSource, refreshDataSource, previewDataSource,
} from '../api/lens'
import type {
  LensDataSource, LensHop, LensHopTrait, LensAggregateConfig, LensCup, LensCupSchema,
} from '../types'
import {
  ChevronLeft, Database, RefreshCw, Save, Plus, Trash2,
  ArrowRight, ArrowLeft, ChevronDown, ChevronUp, Info,
} from 'lucide-react'
import { Modal } from '../components/Modal'
import { Button } from '../components/ui/button'
import { Select, SelectItem } from '../components/ui/select'

type PageTab = 'config' | 'preview'
type WizardStep = 1 | 2 | 3

const TYPE_BADGE: Record<string, string> = {
  numeric:     'bg-blue-50 text-blue-600',
  date:        'bg-emerald-50 text-emerald-600',
  categorical: 'bg-amber-50 text-amber-700',
  boolean:     'bg-violet-50 text-violet-600',
  text:        'bg-paper-sunken text-ink-dim',
}

// ── AddHopModal ───────────────────────────────────────────────────────────────

interface AddHopModalProps {
  currentHop: LensHop
  allCups: LensCup[]
  onAdd: (hop: LensHop) => void
  onClose: () => void
}

function AddHopModal({ currentHop, allCups, onAdd, onClose }: AddHopModalProps) {
  const [direction, setDirection] = useState<'child_to_parent' | 'parent_to_children'>('child_to_parent')
  const [parentSchema, setParentSchema]   = useState<LensCupSchema | null>(null)
  const [childCupId, setChildCupId]       = useState('')
  const [childSchema, setChildSchema]     = useState<LensCupSchema | null>(null)
  const [relLabel, setRelLabel]           = useState('')
  const [selectedTraits, setSelectedTraits] = useState<LensHopTrait[]>([])
  const [loading, setLoading]             = useState(false)

  // Load the current hop's cup schema to discover outgoing relations (child→parent)
  useEffect(() => {
    if (direction === 'child_to_parent' && currentHop.cup_id) {
      setLoading(true)
      getLensCupSchema(currentHop.cup_id)
        .then(setParentSchema)
        .finally(() => setLoading(false))
    }
  }, [direction, currentHop.cup_id])

  // When a child cup is selected (parent→children), load its schema
  useEffect(() => {
    if (direction === 'parent_to_children' && childCupId) {
      setLoading(true)
      getLensCupSchema(childCupId)
        .then((s) => {
          setChildSchema(s)
          setRelLabel('')
          setSelectedTraits([])
        })
        .finally(() => setLoading(false))
    }
  }, [direction, childCupId])

  // When a relation is selected in child→parent mode, load the parent cup schema
  const [hopCupId, setHopCupId]     = useState('')
  const [hopCupTitle, setHopCupTitle] = useState('')
  const [hopSchema, setHopSchema]   = useState<LensCupSchema | null>(null)

  const handleRelationSelect = async (label: string, parentCupId: string, parentCupTitle: string) => {
    setRelLabel(label)
    setHopCupId(parentCupId)
    setHopCupTitle(parentCupTitle)
    setSelectedTraits([])
    if (parentCupId) {
      setLoading(true)
      getLensCupSchema(parentCupId).then(setHopSchema).finally(() => setLoading(false))
    }
  }

  const toggleTrait = (ct_id: string, title: string) => {
    setSelectedTraits((prev) =>
      prev.find((t) => t.cup_trait_id === ct_id)
        ? prev.filter((t) => t.cup_trait_id !== ct_id)
        : [...prev, { cup_trait_id: ct_id, alias: title.toLowerCase().replace(/\s+/g, '_') }]
    )
  }

  const updateAlias = (ct_id: string, alias: string) => {
    setSelectedTraits((prev) => prev.map((t) => t.cup_trait_id === ct_id ? { ...t, alias } : t))
  }

  const canAdd = direction === 'child_to_parent'
    ? relLabel && hopCupId && selectedTraits.length > 0
    : childCupId && relLabel && selectedTraits.length > 0

  const handleAdd = () => {
    if (!canAdd) return
    const cup_id    = direction === 'child_to_parent' ? hopCupId : childCupId
    const cup_title = direction === 'child_to_parent'
      ? hopCupTitle
      : allCups.find((c) => c.id === childCupId)?.title ?? childCupId

    onAdd({
      cup_id,
      cup_title,
      direction,
      relation_label: relLabel,
      traits: selectedTraits,
      filters: [],
      include_created_at: false,
      created_at_alias: 'created_at',
    })
  }

  // For parent→children, filter child cup relations to those pointing to currentHop.cup_id
  const connectingRelations = childSchema?.relations.filter(
    (r) => r.parent_cup_id === currentHop.cup_id
  ) ?? []

  const activeSchema = direction === 'child_to_parent' ? hopSchema : childSchema

  return (
    <Modal title="Add hop" onClose={onClose} size="lg">
      <div className="space-y-5">
          {/* Direction */}
          <div>
            <label className="block text-theme-xs font-medium text-ink-dim mb-2">Direction</label>
            <div className="flex gap-2">
              {([
                { val: 'child_to_parent', label: 'Child → Parent', desc: 'Follow a relation upward (e.g. element → article)', icon: <ArrowLeft size={12} /> },
                { val: 'parent_to_children', label: 'Parent → Children', desc: 'Expand into child records (e.g. preorder → elements)', icon: <ArrowRight size={12} /> },
              ] as const).map(({ val, label, desc, icon }) => (
                <button
                  key={val}
                  onClick={() => { setDirection(val); setRelLabel(''); setSelectedTraits([]) }}
                  className={`flex-1 text-left px-3 py-2.5 rounded border text-theme-xs transition-all ${
                    direction === val ? 'border-accent bg-accent-soft text-accent' : 'border-paper-border text-ink-dim hover:border-paper-border'
                  }`}
                >
                  <div className="flex items-center gap-1.5 font-medium mb-0.5">{icon} {label}</div>
                  <div className="text-[10.5px] text-ink-dim">{desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Child→Parent: pick a relation from the current cup */}
          {direction === 'child_to_parent' && (
            <div>
              <label className="block text-theme-xs font-medium text-ink-dim mb-2">
                Relation from <span className="text-accent">{currentHop.cup_title}</span>
              </label>
              {loading && <p className="text-theme-xs text-ink-dim">Loading relations…</p>}
              {parentSchema && parentSchema.relations.length === 0 && (
                <p className="text-theme-xs text-ink-dim">No relations found on this cup.</p>
              )}
              <div className="space-y-1">
                {parentSchema?.relations.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => handleRelationSelect(r.label, r.parent_cup_id ?? '', r.label)}
                    className={`w-full text-left px-3 py-2 rounded border text-sm transition-all ${
                      relLabel === r.label ? 'border-accent bg-accent-soft text-accent' : 'border-paper-border text-ink-dim hover:border-paper-border'
                    }`}
                  >
                    <span className="font-medium">{r.label}</span>
                    {r.parent_cup_id && <span className="text-ink-dim ml-2 text-theme-xs">→ parent cup</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Parent→Children: pick target cup */}
          {direction === 'parent_to_children' && (
            <div>
              <label className="block text-theme-xs font-medium text-ink-dim mb-2">Child cup</label>
              <Select value={childCupId} onValueChange={setChildCupId} placeholder="Select child cup…">
                {allCups.filter((c) => c.id !== currentHop.cup_id).map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                ))}
              </Select>

              {childCupId && connectingRelations.length > 0 && (
                <div className="mt-3">
                  <label className="block text-theme-xs font-medium text-ink-dim mb-1">
                    Relation on child that links to <span className="text-accent">{currentHop.cup_title}</span>
                  </label>
                  <div className="space-y-1">
                    {connectingRelations.map((r) => (
                      <button
                        key={r.id}
                        onClick={() => setRelLabel(r.label)}
                        className={`w-full text-left px-3 py-2 rounded border text-sm transition-all ${
                          relLabel === r.label ? 'border-accent bg-accent-soft text-accent' : 'border-paper-border text-ink-dim hover:border-paper-border'
                        }`}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {childCupId && connectingRelations.length === 0 && !loading && (
                <div className="mt-3">
                  <label className="block text-theme-xs font-medium text-ink-dim mb-1">
                    Relation label on child cup
                  </label>
                  <input
                    value={relLabel}
                    onChange={(e) => setRelLabel(e.target.value)}
                    placeholder="e.g. preorder"
                    className="field w-full"
                  />
                  <p className="text-[10.5px] text-ink-dim mt-1">
                    Enter the relation label that links the child to the current cup.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Trait picker — shown once we know the target cup */}
          {(hopSchema || (childCupId && childSchema)) && (
            <div>
              <label className="block text-theme-xs font-medium text-ink-dim mb-2">
                Traits to pull <span className="text-ink-dim font-normal">— set an alias for each</span>
              </label>
              <div className="space-y-1">
                {(activeSchema?.traits ?? []).map((t) => {
                  const sel = selectedTraits.find((s) => s.cup_trait_id === t.cup_trait_id)
                  return (
                    <div key={t.cup_trait_id} className={`flex items-center gap-2 px-3 py-2 rounded border text-sm transition-colors ${sel ? 'border-accent/30 bg-accent-soft' : 'border-paper-border hover:border-paper-border'}`}>
                      <input
                        type="checkbox"
                        checked={!!sel}
                        onChange={() => toggleTrait(t.cup_trait_id, t.title)}
                        className="rounded accent-indigo-600 shrink-0"
                      />
                      <span className="flex-1 text-ink">{t.title}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 ${TYPE_BADGE[t.type] ?? TYPE_BADGE.text}`}>{t.type}</span>
                      {sel && (
                        <input
                          value={sel.alias}
                          onChange={(e) => updateAlias(t.cup_trait_id, e.target.value)}
                          placeholder="alias"
                          className="field w-28 text-theme-xs py-0.5"
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-paper-border">
            <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={handleAdd} disabled={!canAdd}>Add hop</Button>
          </div>
      </div>
    </Modal>
  )
}

// ── HopCard ───────────────────────────────────────────────────────────────────

function HopCard({
  hop, index, onRemove, onChange,
}: {
  hop: LensHop
  index: number
  onRemove: () => void
  onChange: (updated: LensHop) => void
}) {
  const [expanded, setExpanded]   = useState(true)
  const [schema, setSchema]       = useState<LensCupSchema | null>(null)
  const [schemaLoading, setSchemaLoading] = useState(false)
  const isRoot = hop.direction === 'root'

  // Fetch this hop's cup schema so we can show checkboxes
  useEffect(() => {
    if (!hop.cup_id) return
    setSchemaLoading(true)
    getLensCupSchema(hop.cup_id)
      .then(setSchema)
      .finally(() => setSchemaLoading(false))
  }, [hop.cup_id])

  const toggleTrait = (ct_id: string, title: string) => {
    const existing = hop.traits.find((t) => t.cup_trait_id === ct_id)
    if (existing) {
      onChange({ ...hop, traits: hop.traits.filter((t) => t.cup_trait_id !== ct_id) })
    } else {
      onChange({
        ...hop,
        traits: [...hop.traits, { cup_trait_id: ct_id, alias: title.toLowerCase().replace(/\s+/g, '_') }],
      })
    }
  }

  const updateAlias = (ct_id: string, alias: string) => {
    onChange({ ...hop, traits: hop.traits.map((t) => t.cup_trait_id === ct_id ? { ...t, alias } : t) })
  }

  return (
    <div className="border border-paper-border rounded-lg overflow-hidden">
      {/* Header row */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-paper-sunken/60 select-none"
        onClick={() => setExpanded((e) => !e)}
      >
        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-theme-xs font-semibold shrink-0 ${isRoot ? 'bg-accent text-white' : 'bg-paper-border text-ink-dim'}`}>
          {index}
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-ink">{hop.cup_title}</span>
          {isRoot && <span className="ml-2 text-[10px] text-accent">root</span>}
          {!isRoot && (
            <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded font-medium ${
              hop.direction === 'child_to_parent' ? 'bg-sky-50 text-sky-600' : 'bg-violet-50 text-violet-600'
            }`}>
              {hop.direction === 'child_to_parent' ? '↑ child→parent' : '↓ parent→children'}
            </span>
          )}
          {hop.relation_label && (
            <span className="ml-2 text-[10px] text-ink-dim">via "{hop.relation_label}"</span>
          )}
        </div>
        <span className="text-theme-xs text-ink-dim shrink-0">
          {hop.traits.length} trait{hop.traits.length !== 1 ? 's' : ''} selected
        </span>
        {!isRoot && (
          <button
            onClick={(e) => { e.stopPropagation(); onRemove() }}
            className="p-1 text-ink-dim hover:text-red-500 hover:bg-red-50 rounded transition-colors"
          >
            <Trash2 size={12} />
          </button>
        )}
        {expanded ? <ChevronUp size={13} className="text-ink-dim shrink-0" /> : <ChevronDown size={13} className="text-ink-dim shrink-0" />}
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-paper-border pt-3">

          {/* created_at — root only */}
          {isRoot && (
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-theme-xs text-ink-dim cursor-pointer">
                <input
                  type="checkbox"
                  checked={hop.include_created_at}
                  onChange={(e) => onChange({ ...hop, include_created_at: e.target.checked })}
                  className="rounded accent-indigo-600"
                />
                Include <code className="text-[10.5px] bg-paper-sunken px-1 rounded">created_at</code> as a date column
              </label>
              {hop.include_created_at && (
                <div className="flex items-center gap-1.5">
                  <span className="text-theme-xs text-ink-dim">alias:</span>
                  <input
                    value={hop.created_at_alias}
                    onChange={(e) => onChange({ ...hop, created_at_alias: e.target.value })}
                    className="field w-28 text-theme-xs py-0.5"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              )}
            </div>
          )}

          {/* Trait picker — checkboxes from the cup's schema */}
          {schemaLoading && (
            <p className="text-theme-xs text-ink-dim">Loading traits…</p>
          )}
          {!schemaLoading && schema && schema.traits.length === 0 && (
            <p className="text-theme-xs text-ink-dim italic">This cup has no traits.</p>
          )}
          {!schemaLoading && schema && schema.traits.length > 0 && (
            <div>
              <p className="text-theme-xs font-medium text-ink-dim uppercase tracking-wide mb-1.5">Traits</p>
              <div className="space-y-1">
                {schema.traits.map((t) => {
                  const sel = hop.traits.find((s) => s.cup_trait_id === t.cup_trait_id)
                  return (
                    <div
                      key={t.cup_trait_id}
                      className={`flex items-center gap-2 px-3 py-2 rounded border text-sm transition-colors ${
                        sel ? 'border-accent/30 bg-accent-soft' : 'border-paper-border hover:border-paper-border'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={!!sel}
                        onChange={() => toggleTrait(t.cup_trait_id, t.title)}
                        className="rounded accent-indigo-600 shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="flex-1 text-ink">{t.title}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 ${TYPE_BADGE[t.type] ?? TYPE_BADGE.text}`}>
                        {t.type}
                      </span>
                      {sel && (
                        <>
                          <ArrowRight size={11} className="text-ink-dim shrink-0" />
                          <input
                            value={sel.alias}
                            onChange={(e) => updateAlias(t.cup_trait_id, e.target.value)}
                            placeholder="alias"
                            className="field w-28 text-theme-xs py-0.5"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}


// ── Main page ─────────────────────────────────────────────────────────────────

export function LensDataSourceDetail() {
  const { id } = useParams<{ id: string }>()
  const isNew  = id === 'new'
  const navigate = useNavigate()

  const [pageTab, setPageTab]   = useState<PageTab>('config')
  const [step, setStep]         = useState<WizardStep>(1)

  // Config state
  const [name, setName]               = useState('')
  const [description, setDescription] = useState('')
  const [rootCup, setRootCup]         = useState<LensCup | null>(null)
  const [hops, setHops]               = useState<LensHop[]>([])
  const [outputMode, setOutputMode]   = useState<'enrich' | 'aggregate'>('enrich')
  const [aggConfig, setAggConfig]     = useState<LensAggregateConfig>({ group_by: [], aggregations: [] })

  // Data
  const [ds, setDs]               = useState<LensDataSource | null>(null)
  const [cups, setCups]           = useState<LensCup[]>([])
  const [saving, setSaving]       = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [showAddHop, setShowAddHop] = useState(false)
  const [preview, setPreview]     = useState<{ columns: string[]; rows: unknown[][] } | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  useEffect(() => { getLensCups().then(setCups) }, [])

  useEffect(() => {
    if (isNew || !id) return
    getDataSource(id).then((d) => {
      setDs(d)
      setName(d.name)
      setDescription(d.description ?? '')
      setHops(d.hops)
      setOutputMode(d.output_mode)
      if (d.aggregate_config) setAggConfig(d.aggregate_config)
      setRootCup({ id: d.root_cup_id, title: d.root_cup_title })
    })
  }, [id, isNew])

  const handleRootCupSelect = useCallback(async (cup: LensCup) => {
    setRootCup(cup)
    setHops([{
      cup_id: cup.id,
      cup_title: cup.title,
      direction: 'root',
      relation_label: undefined,
      traits: [],
      filters: [],
      include_created_at: false,
      created_at_alias: 'created_at',
    }])
  }, [])

  const addHop = (hop: LensHop) => {
    setHops((prev) => [...prev, hop])
    setShowAddHop(false)
  }

  const removeHop = (index: number) => {
    setHops((prev) => prev.filter((_, i) => i !== index))
  }

  const updateHop = (index: number, updated: LensHop) => {
    setHops((prev) => prev.map((h, i) => (i === index ? updated : h)))
  }

  const handleSave = async () => {
    if (!rootCup) return
    setSaving(true)
    try {
      const payload = {
        name,
        description: description || undefined,
        root_cup_id: rootCup.id,
        root_cup_title: rootCup.title,
        output_mode: outputMode,
        hops,
        aggregate_config: outputMode === 'aggregate' ? aggConfig : null,
      }
      if (isNew) {
        const created = await createDataSource(payload)
        navigate(`/lens/datasources/${created.id}`, { replace: true })
      } else if (ds) {
        const updated = await updateDataSource(ds.id, payload)
        setDs(updated)
      }
    } finally {
      setSaving(false)
    }
  }

  const handleRefresh = async () => {
    if (!ds) return
    setRefreshing(true)
    try {
      const result = await refreshDataSource(ds.id)
      setDs((prev) => prev ? { ...prev, cached_at: result.cached_at, n_rows: result.n_rows, columns: result.columns as any } : prev)
    } catch (err: any) {
      alert(err.response?.data?.detail ?? 'Refresh failed')
    } finally {
      setRefreshing(false)
    }
  }

  const loadPreview = async () => {
    if (!ds) return
    setPreviewLoading(true)
    try {
      const data = await previewDataSource(ds.id)
      setPreview(data)
    } catch {
      setPreview(null)
    } finally {
      setPreviewLoading(false)
    }
  }

  useEffect(() => {
    if (pageTab === 'preview' && ds?.cached_at && !preview) {
      loadPreview()
    }
  }, [pageTab, ds?.cached_at])

  // Derived columns from hops (for aggregate config)
  const allAliases: string[] = hops.flatMap((h) => {
    const traits = h.traits.map((t) => t.alias)
    if (h.include_created_at) traits.push(h.created_at_alias || 'created_at')
    return traits
  })

  const canStep1 = name.trim() && rootCup
  const currentLastHop = hops[hops.length - 1] ?? null

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <Link to="/lens/datasources" className="text-ink-dim hover:text-ink-dim transition-colors">
          <ChevronLeft size={18} />
        </Link>
        <Database size={15} className="text-accent" />
        <h1 className="text-xl font-semibold text-ink flex-1">
          {isNew ? 'New Data Source' : name}
        </h1>

        <div className="flex items-center gap-2">
          {!isNew && ds && (
            <Button
              variant="secondary"
              size="sm"
              icon={RefreshCw}
              loading={refreshing}
              onClick={handleRefresh}
              disabled={refreshing || !ds.hops?.length}
              title="Re-fetch from Brainer and materialise into cache"
            >
              {refreshing ? 'Refreshing…' : ds.cached_at ? 'Refresh data' : 'Fetch & cache'}
            </Button>
          )}
          <Button variant="primary" size="sm" icon={Save} onClick={handleSave} disabled={saving || !canStep1}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Cache status banner */}
      {!isNew && ds && (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-theme-xs mb-4 ${
          ds.cached_at ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
        }`}>
          <Info size={13} />
          {ds.cached_at
            ? `Cached — ${ds.n_rows.toLocaleString()} rows, ${ds.columns.length} columns · Last refreshed ${new Date(ds.cached_at).toLocaleString()}`
            : 'Not yet cached — save the configuration and click "Fetch & cache" to materialise the data.'}
        </div>
      )}

      {/* Page tabs */}
      {!isNew && (
        <div className="flex gap-0 border-b border-paper-border mb-5">
          {(['config', 'preview'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setPageTab(tab)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors capitalize ${
                pageTab === tab ? 'border-accent text-accent' : 'border-transparent text-gray-500 hover:text-gray-700-dim hover:text-ink'
              }`}
            >
              {tab === 'config' ? 'Configuration' : 'Preview'}
            </button>
          ))}
        </div>
      )}

      {/* ── Config tab ──────────────────────────────────────────────────────── */}
      {(isNew || pageTab === 'config') && (
        <div>
          {/* Wizard steps (new mode) */}
          {isNew && (
            <div className="flex gap-1 border-b border-paper-border mb-4">
              {(['Basics', 'Hops', 'Output'] as const).map((label, i) => {
                const s = (i + 1) as WizardStep
                return (
                  <button
                    key={label}
                    onClick={() => setStep(s)}
                    className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                      step === s ? 'border-accent text-accent' : 'border-transparent text-gray-500 hover:text-gray-700-dim hover:text-ink'
                    }`}
                  >
                    {i + 1}. {label}
                  </button>
                )
              })}
            </div>
          )}

          {/* Step 1: Basics */}
          {(step === 1 || !isNew) && (
            <div className="space-y-5 mb-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-theme-xs font-medium text-ink-dim mb-1">Name</label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Article Sales"
                    className="field w-full"
                  />
                </div>
                <div>
                  <label className="block text-theme-xs font-medium text-ink-dim mb-1">Description <span className="text-ink-dim font-normal">(optional)</span></label>
                  <input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="What this dataset represents"
                    className="field w-full"
                  />
                </div>
              </div>

              <div>
                <label className="block text-theme-xs font-medium text-ink-dim mb-2">Root cup <span className="text-ink-dim font-normal">(starting point)</span></label>
                <div className="grid grid-cols-2 gap-2">
                  {cups.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => handleRootCupSelect(c)}
                      className={`text-left px-3 py-2.5 rounded border text-sm transition-all ${
                        rootCup?.id === c.id
                          ? 'border-accent bg-accent-soft text-accent font-medium'
                          : 'border-paper-border text-ink hover:border-paper-border'
                      }`}
                    >
                      {c.title}
                    </button>
                  ))}
                </div>
              </div>

              {isNew && (
                <div className="flex justify-end pt-2">
                  <Button variant="primary" size="sm" onClick={() => setStep(2)} disabled={!canStep1}>
                    Next: Hops →
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Hops */}
          {(step === 2 || !isNew) && (
            <div className="space-y-3 mb-6">
              {!isNew && <h2 className="text-theme-xs font-semibold text-ink-dim mb-2">Hops</h2>}

              {hops.length === 0 && (
                <p className="text-sm text-ink-dim italic">Select a root cup first to start adding hops.</p>
              )}

              {hops.map((hop, i) => (
                <HopCard
                  key={i}
                  hop={hop}
                  index={i}
                  onRemove={() => removeHop(i)}
                  onChange={(updated) => updateHop(i, updated)}
                />
              ))}

              {rootCup && hops.length < 6 && (
                <button
                  onClick={() => setShowAddHop(true)}
                  className="w-full border border-dashed border-paper-border rounded-lg py-2.5 text-sm text-ink-dim hover:border-accent/60 hover:text-accent hover:bg-accent-soft/60 transition-colors flex items-center justify-center gap-1.5"
                >
                  <Plus size={13} /> Add hop
                </button>
              )}

              {isNew && (
                <div className="flex justify-between pt-2">
                  <Button variant="secondary" size="sm" onClick={() => setStep(1)}>← Back</Button>
                  <Button variant="primary" size="sm" onClick={() => setStep(3)} disabled={hops.length === 0}>Next: Output →</Button>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Output mode + aggregate config */}
          {(step === 3 || !isNew) && (
            <div className="space-y-5 mb-6">
              {!isNew && <h2 className="text-theme-xs font-semibold text-ink-dim mb-2">Output mode</h2>}

              <div className="flex gap-3">
                {([
                  { val: 'enrich', label: 'Enrich', desc: 'One row per root record with parent traits joined in' },
                  { val: 'aggregate', label: 'Aggregate', desc: 'Group and aggregate columns (e.g. sum sales per article)' },
                ] as const).map(({ val, label, desc }) => (
                  <button
                    key={val}
                    onClick={() => setOutputMode(val)}
                    className={`flex-1 text-left px-3 py-2.5 rounded border text-theme-xs transition-all ${
                      outputMode === val ? 'border-accent bg-accent-soft text-accent' : 'border-paper-border text-ink-dim hover:border-paper-border'
                    }`}
                  >
                    <div className="font-medium mb-0.5">{label}</div>
                    <div className="text-[10.5px] text-ink-dim">{desc}</div>
                  </button>
                ))}
              </div>

              {outputMode === 'aggregate' && allAliases.length > 0 && (
                <div className="space-y-4 border border-paper-border rounded-lg p-4">
                  <div>
                    <label className="block text-theme-xs font-medium text-ink-dim mb-2">Group by columns</label>
                    <div className="flex flex-wrap gap-1.5">
                      {allAliases.map((alias) => {
                        const active = aggConfig.group_by.includes(alias)
                        return (
                          <button
                            key={alias}
                            onClick={() => setAggConfig((prev) => ({
                              ...prev,
                              group_by: active ? prev.group_by.filter((g) => g !== alias) : [...prev.group_by, alias],
                            }))}
                            className={`px-2.5 py-1 rounded text-theme-xs font-medium transition-colors ${
                              active ? 'bg-accent text-white' : 'bg-paper-sunken text-ink-dim hover:bg-paper-border'
                            }`}
                          >
                            {alias}
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="block text-theme-xs font-medium text-ink-dim mb-2">Aggregations</label>
                    <div className="space-y-2">
                      {allAliases.filter((a) => !aggConfig.group_by.includes(a)).map((alias) => {
                        const existing = aggConfig.aggregations.find((a) => a.column === alias)
                        return (
                          <div key={alias} className="flex items-center gap-2 text-theme-xs">
                            <input
                              type="checkbox"
                              checked={!!existing}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setAggConfig((prev) => ({
                                    ...prev,
                                    aggregations: [...prev.aggregations, { column: alias, function: 'sum', alias: `total_${alias}` }],
                                  }))
                                } else {
                                  setAggConfig((prev) => ({
                                    ...prev,
                                    aggregations: prev.aggregations.filter((a) => a.column !== alias),
                                  }))
                                }
                              }}
                              className="rounded accent-indigo-600"
                            />
                            <span className="text-ink w-28 truncate">{alias}</span>
                            {existing && (
                              <>
                                <Select
                                  value={existing.function}
                                  onValueChange={(v) => setAggConfig((prev) => ({
                                    ...prev,
                                    aggregations: prev.aggregations.map((a) =>
                                      a.column === alias ? { ...a, function: v as any } : a
                                    ),
                                  }))}
                                  className="w-24 text-theme-xs py-0.5"
                                >
                                  {['sum', 'count', 'avg', 'min', 'max'].map((f) => (
                                    <SelectItem key={f} value={f}>{f}</SelectItem>
                                  ))}
                                </Select>
                                <ArrowRight size={11} className="text-ink-dim shrink-0" />
                                <input
                                  value={existing.alias}
                                  onChange={(e) => setAggConfig((prev) => ({
                                    ...prev,
                                    aggregations: prev.aggregations.map((a) =>
                                      a.column === alias ? { ...a, alias: e.target.value } : a
                                    ),
                                  }))}
                                  className="field w-32 text-theme-xs py-0.5"
                                  placeholder="output alias"
                                />
                              </>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              )}

              {isNew && (
                <div className="flex justify-between pt-2">
                  <Button variant="secondary" size="sm" onClick={() => setStep(2)}>← Back</Button>
                  <Button variant="primary" size="sm" icon={Save} onClick={handleSave} disabled={saving || !canStep1}>
                    {saving ? 'Saving…' : 'Save data source'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Preview tab ──────────────────────────────────────────────────────── */}
      {!isNew && pageTab === 'preview' && (
        <div>
          {previewLoading ? (
            <div className="flex items-center gap-2 text-sm text-ink-dim py-8">
              <RefreshCw size={13} className="animate-spin" /> Loading preview…
            </div>
          ) : !preview ? (
            <div className="text-center py-12">
              <p className="text-sm text-ink-dim">
                {ds?.cached_at ? 'Could not load preview.' : 'Refresh the data source to see a preview.'}
              </p>
            </div>
          ) : (
            <div>
              <p className="text-theme-xs text-ink-dim mb-3">Showing first 50 rows · {preview.columns.length} columns</p>
              <div className="rounded-xl border border-gray-200 bg-white overflow-x-auto">
              <table className="data-table text-theme-xs border-collapse">
                <thead>
                  <tr>
                    {preview.columns.map((col) => (
                      <th key={col} className="border-r border-paper-border last:border-r-0">
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((row, ri) => (
                    <tr key={ri}>
                      {(row as unknown[]).map((cell, ci) => (
                        <td key={ci} className="border-r border-paper-border last:border-r-0 max-w-[200px] truncate">
                          {cell === null || cell === undefined ? <span className="text-ink-dim">—</span> : String(cell)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add hop modal */}
      {showAddHop && currentLastHop && (
        <AddHopModal
          currentHop={currentLastHop}
          allCups={cups}
          onAdd={addHop}
          onClose={() => setShowAddHop(false)}
        />
      )}
    </div>
  )
}
